<template>
  <!--
    Abaixo de md a busca ocupa a linha inteira e os sete chips viram uma faixa
    rolável, em vez de quebrarem em três ou quatro linhas empilhadas. Mesmo
    padrão do header do Dashboard (components/Dashboard/Header/index.vue).
  -->
  <div class="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:gap-4">
    <div class="w-full md:w-[240px]">
      <SearchInput
        v-model="searchText"
        data-testid="handover-search-input"
        width="100%"
        label="Buscar"
        hide-details
        density="compact"
        @input="onSearch"
      />
    </div>

    <div
      class="flex items-center gap-2 flex-nowrap overflow-x-auto pb-1 md:flex-wrap md:overflow-visible md:pb-0"
    >
      <div class="relative shrink-0">
        <DateFilter
          title="Período"
          data-testid="handover-shift-date-filter"
          @select="handleShiftDateFilter"
        />
      </div>

      <div class="relative shrink-0">
        <DateFilter
          title="Expiração"
          data-testid="handover-expiration-date-filter"
          @select="handleExpirationDateFilter"
        />
      </div>

      <!--
        Unidade antes de Escala: a escala pertence à unidade, e é a unidade que
        estreita a lista de escalas. Ler da esquerda para a direita passa a
        seguir a mesma ordem em que os filtros se afetam — igual ao Dashboard,
        onde `hospitals-filter` vem antes de `scales-filter`.
      -->
      <div class="relative shrink-0" @click.stop="toggleFilter('unit')">
        <SelectFilter
          label="Unidades"
          data-testid="handover-unit-filter"
          :icon="hospitalIcon"
          :options="healthUnitOptions"
          :is-open="openFilterName === 'unit'"
          :selected-value="selectedHealthUnitId"
          searchable
          dropdown-width="w-64"
          @select="handleHealthUnitSelect"
        />
      </div>

      <div class="relative shrink-0" @click.stop="toggleFilter('schedule')">
        <SelectFilter
          label="Escalas"
          data-testid="handover-schedule-filter"
          :icon="scaleIcon"
          :options="scheduleOptions"
          :is-open="openFilterName === 'schedule'"
          :selected-value="selectedScheduleId"
          searchable
          dropdown-width="w-64"
          @select="handleScheduleSelect"
        />
      </div>

      <div class="relative shrink-0" @click.stop="toggleFilter('operationalGroup')">
        <SelectFilter
          label="Grupo Operacional"
          data-testid="handover-operational-group-filter"
          :options="operationalGroupOptions"
          :is-open="openFilterName === 'operationalGroup'"
          :selected-value="modelValue.operationalGroups ?? []"
          multiple
          searchable
          dropdown-width="w-64"
          @select="toggleOperationalGroup"
        />
      </div>

      <div
        v-if="context === 'pending'"
        class="relative shrink-0"
        @click.stop="toggleFilter('expirationBucket')"
      >
        <SelectFilter
          label="Situação"
          data-testid="handover-expiration-bucket-filter"
          :options="expirationBucketOptions"
          :is-open="openFilterName === 'expirationBucket'"
          :selected-value="modelValue.expirationBucket ?? null"
          dropdown-width="w-56"
          @select="selectExpirationBucket"
        />
      </div>

      <div class="relative shrink-0" @click.stop="toggleFilter('status')">
        <SelectFilter
          label="Status"
          data-testid="handover-status-filter"
          :options="statusOptions"
          :is-open="openFilterName === 'status'"
          :selected-value="modelValue.statuses ?? []"
          multiple
          dropdown-width="w-56"
          align-right
          @select="toggleStatus"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import DateFilter from '@/components/Filters/DateFilter.vue'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'
import { useErrorToast } from '@/composables/useErrorToast'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import { useOperationalGroups } from '@/hooks/useOperationalGroups'
import {
  HandoverStatusFilter,
  ExpirationBucket
} from '@/Network/Types/Requests/HandoverInviteRequests'
import ScaleIcon from '@/assets/icons/scale.vue'
import HospitalIcon from '@/assets/icons/hospital.vue'
import type { HandoverFilterValues } from './handoverFilters.types'

interface DateRange {
  start: Date | null
  end: Date | null
}

const props = defineProps<{
  modelValue: HandoverFilterValues
  context: 'pending' | 'history'
}>()

const emit = defineEmits<{
  'update:modelValue': [value: HandoverFilterValues]
}>()

const scheduleStore = useScheduleStore()
const { notifyError } = useErrorToast()
const healthUnitStore = useHealthUnitStore()
const { options: operationalGroupOptions, load: loadOperationalGroups } = useOperationalGroups()

const searchText = ref(props.modelValue.query ?? '')
const openFilterName = ref<string | null>(null)

const scaleIcon = ScaleIcon
const hospitalIcon = HospitalIcon

const EXPIRATION_BUCKET_LABELS: Record<ExpirationBucket, string> = {
  [ExpirationBucket.CRITICAL]: 'Crítico (menos de 24h)',
  [ExpirationBucket.WARNING]: 'Atenção (até 3 dias)',
  [ExpirationBucket.ON_TIME]: 'No prazo (mais de 3 dias)'
}

// Ordem do contrato C1: do início do fluxo (convite enviado) ao desfecho.
const STATUS_OPTIONS: { value: HandoverStatusFilter; label: string }[] = [
  { value: HandoverStatusFilter.IN_TRANSITION, label: 'Em transição' },
  { value: HandoverStatusFilter.IN_ADMIN_QUEUE, label: 'Em fila de aprovação' },
  { value: HandoverStatusFilter.APPROVED, label: 'Aprovado' },
  { value: HandoverStatusFilter.REJECTED, label: 'Reprovado' },
  // Mesmo rótulo do badge da linha: quem vê "Cancelado" na tabela precisa achar
  // a opção com esse nome no filtro.
  { value: HandoverStatusFilter.CANCELLED_BY_REQUESTER, label: 'Cancelado' },
  { value: HandoverStatusFilter.EXPIRED, label: 'Expirado' }
]

const expirationBucketOptions = Object.entries(EXPIRATION_BUCKET_LABELS).map(([value, label]) => ({
  value,
  label
}))

const statusOptions = STATUS_OPTIONS

// `listSchedules` (GET /schedules) é a lista plana de escalas de verdade.
// `schedulesForPage` (GET /schedules/list) é a árvore empresa → unidade →
// escala: o nível de topo ali é a EMPRESA, então este filtro exibia nomes de
// empresa e mandava company.id como scheduleId — o backend filtrava
// `schedule.id = <companyId>` e a listagem voltava vazia.
const scheduleOptions = computed(() =>
  scheduleStore.listSchedules.map((schedule) => ({
    label: schedule.title,
    value: schedule.value
  }))
)

// `listHealthUnits` vem de `/healthUnits/list-view`, que não é paginado.
// `healthUnits.data` vem do endpoint paginado com `limit: 15`: o dropdown
// mostrava só as 15 primeiras unidades, e como a busca do SelectFilter é
// client-side, digitar o nome de uma unidade fora dessas 15 também não
// encontrava nada.
const healthUnitOptions = computed(() =>
  healthUnitStore.listHealthUnits.map((unit) => ({
    label: unit.title,
    value: unit.value
  }))
)

const selectedScheduleId = computed(() =>
  props.modelValue.scheduleId ? String(props.modelValue.scheduleId) : null
)

const selectedHealthUnitId = computed(() =>
  props.modelValue.healthUnitId ? String(props.modelValue.healthUnitId) : null
)

const update = (patch: Partial<HandoverFilterValues>) => {
  emit('update:modelValue', { ...props.modelValue, ...patch })
}

// O SelectFilter emite um valor por vez; acumular/remover é responsabilidade daqui.
const toggleInList = <T,>(list: T[] | undefined, value: T): T[] => {
  const current = list ?? []
  return current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
}

const toggleFilter = (name: string) => {
  openFilterName.value = openFilterName.value === name ? null : name
}

const closeFilters = () => {
  openFilterName.value = null
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null

const onSearch = () => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => update({ query: searchText.value }), 400)
}

const handleShiftDateFilter = (range: DateRange) => {
  update({ startDate: range.start?.toISOString(), endDate: range.end?.toISOString() })
}

const handleExpirationDateFilter = (range: DateRange) => {
  update({
    expirationStartDate: range.start?.toISOString(),
    expirationEndDate: range.end?.toISOString()
  })
}

const handleScheduleSelect = (value: string) => update({ scheduleId: Number(value) })

const handleHealthUnitSelect = (value: string) => update({ healthUnitId: Number(value) })

const toggleOperationalGroup = (value: string) => {
  update({ operationalGroups: toggleInList(props.modelValue.operationalGroups, value) })
}

const selectExpirationBucket = (value: string) => {
  const bucket = value as ExpirationBucket
  update({ expirationBucket: props.modelValue.expirationBucket === bucket ? undefined : bucket })
}

const toggleStatus = (value: string) => {
  update({
    statuses: toggleInList(props.modelValue.statuses, value as HandoverStatusFilter)
  })
}

defineExpose({
  toggleOperationalGroup,
  selectExpirationBucket,
  toggleStatus,
  onSearch,
  handleShiftDateFilter,
  handleExpirationDateFilter
})

watch(
  () => props.modelValue.query,
  (query) => {
    if (query !== searchText.value) searchText.value = query ?? ''
  }
)

/**
 * Mantém a lista de escalas coerente com a unidade filtrada, no mesmo padrão do
 * Dashboard (`components/Dashboard/Header/filters/ScalesFilter.vue`): recarrega
 * as escalas da unidade e descarta a escala selecionada que deixou de pertencer
 * a ela. Sem a poda, o filtro antigo continua valendo na requisição sem aparecer
 * no dropdown — a tela volta vazia e nada na interface explica por quê.
 */
const syncSchedules = async (healthUnitId: number | undefined) => {
  if (healthUnitId) {
    await scheduleStore.setSchedulesByHealthUnits([healthUnitId])
  } else {
    await scheduleStore.getAllSchedules()
  }

  const selectedId = props.modelValue.scheduleId
  if (!selectedId) return

  const available = new Set(scheduleStore.listSchedules.map((schedule) => schedule.value))
  if (!available.has(String(selectedId))) update({ scheduleId: undefined })
}

watch(
  () => props.modelValue.healthUnitId,
  (healthUnitId) => {
    syncSchedules(healthUnitId).catch((error) =>
      notifyError(error, 'Não foi possível carregar as escalas')
    )
  }
)

onMounted(() => {
  document.addEventListener('click', closeFilters)
  // Escalas não têm cache no store: só busca se a aba ainda não tiver carregado
  // a lista, evitando refetch a cada abertura do modal de histórico. A carga
  // inicial passa pelo `syncSchedules` para já respeitar a unidade filtrada.
  if (scheduleStore.listSchedules.length === 0)
    syncSchedules(props.modelValue.healthUnitId).catch((error) =>
      notifyError(error, 'Não foi possível carregar as escalas')
    )
  // Unidades, ao contrário, carregam sempre: `listHealthUnits` é compartilhado
  // com as telas que o preenchem pelo endpoint paginado, então uma lista curta
  // deixada por outra aba passaria por completa aqui. Mesma carga do Dashboard.
  healthUnitStore.fetchAllHealthUnitsForSelect()
  loadOperationalGroups()
})

onBeforeUnmount(() => {
  document.removeEventListener('click', closeFilters)
  if (debounceTimer) clearTimeout(debounceTimer)
})
</script>
